import React from 'react';
import { Button, Col, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { useNavigation } from '@refinedev/core';
import type { ColumnsType } from 'antd/es/table';
import type { PagedListState } from '@/hooks/usePagedList';
import type { SuspectMatchItem, SuspectMatchesResponse } from '@/types';
import { ListBody, LoadMoreFooter } from './ListParts';
import {
  SEVERITY_COLOR,
  categoriseSuspectReason,
  splitReasonClauses,
  worstSuspectSeverity,
} from './suspectReason';

const { Text } = Typography;

export type SuspectSummary = Omit<SuspectMatchesResponse, 'matches'>;

export const SuspectMatchesTab: React.FC<{
  state: PagedListState<SuspectMatchItem, SuspectSummary>;
}> = ({ state }) => {
  const { show } = useNavigation();
  const summary = state.list?.summary;
  const columns: ColumnsType<SuspectMatchItem> = [
    {
      title: 'Severity',
      width: 90,
      render: (_: unknown, r) => {
        const sev = worstSuspectSeverity(r.suspectedCheatReason);
        return <Tag color={SEVERITY_COLOR[sev]}>{sev.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Player',
      dataIndex: 'userDisplayName',
      render: (name: string | null | undefined, record) => (
        <Button type="link" size="small" onClick={() => show('players', record.userId)}>
          {name || record.userId.slice(0, 8)}
        </Button>
      ),
    },
    { title: 'Match', dataIndex: 'matchType', width: 120 },
    { title: 'Overs', dataIndex: 'overs', width: 80 },
    {
      title: 'User R/O',
      width: 110,
      render: (_: unknown, r) => `${r.userRuns} / ${r.userOvers}`,
    },
    {
      title: 'Opp R/O',
      width: 110,
      render: (_: unknown, r) => `${r.opponentRuns} / ${r.opponentOvers}`,
    },
    {
      title: 'Duration',
      dataIndex: 'durationSeconds',
      width: 100,
      render: (s: number) => `${s}s`,
    },
    {
      title: 'Reason',
      dataIndex: 'suspectedCheatReason',
      width: 260,
      render: (reason: string) => (
        <Space wrap size={[4, 4]}>
          {splitReasonClauses(reason).map((clause, i) => {
            const meta = categoriseSuspectReason(clause);
            return (
              <Tooltip key={i} title={clause}>
                <Tag color={meta.color} style={{ fontSize: 11 }}>
                  {meta.label}
                </Tag>
              </Tooltip>
            );
          })}
        </Space>
      ),
    },
    {
      title: 'When',
      dataIndex: 'createdAt',
      width: 160,
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Matches flagged by server-side cheat detection (§6.5). Reasons include impossible run totals
        (e.g. 9985 in a 50-over match), duration shorter than physically possible, or shot-breakdown
        runs exceeding userRuns (extras-aware). Populated at /match/complete.
      </Text>
      <ListBody state={state}>
        {summary && (
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col>
              <Statistic
                title="Flagged matches"
                value={summary.total}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col>
              <Statistic title="Unique players" value={summary.uniqueUsers} />
            </Col>
            <Col>
              <Statistic title="Reason types" value={summary.byReason.length} />
            </Col>
          </Row>
        )}
        {summary && summary.byReason.length > 0 && (
          <Space wrap style={{ marginBottom: 12 }}>
            {summary.byReason.map((b) => {
              const meta = categoriseSuspectReason(b.reason);
              return (
                <Tooltip key={b.reason} title={b.reason}>
                  <Tag color={meta.color}>
                    {meta.label}: {b.count.toLocaleString()}
                  </Tag>
                </Tooltip>
              );
            })}
          </Space>
        )}
        <Table<SuspectMatchItem>
          dataSource={state.list?.rows ?? []}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 1100 }}
          columns={columns}
        />
        <LoadMoreFooter state={state} noun="matches" />
      </ListBody>
    </div>
  );
};
